import { useCallback, useEffect, useRef, useState } from 'react';
import { ceebAcceptApi, CeebAcceptStatus } from '../services/api';

// Akceptacja JEDNEGO protokołu w CEEB (zielony przycisk superadmina) — PipelineView i TermsTab.
// Zastępuje dwie kopie pętli „60 × 3 s", które po 3 min cichły bez komunikatu i odblokowywały
// przycisk przy wciąż pracującym bocie (przegląd 2026-10-09, W3). Teraz: jeden kanon pollingu,
// guard anty-stale po `jobId` i limit zgodny z limitem bota po stronie serwera.
export interface CeebAcceptOutcome {
  buildingId: string;
  status: 'done' | 'error';
  error?: string;
  mail?: CeebAcceptStatus['mail'];
}

const POLL_INTERVAL_MS = 3000;
// Serwer ubija bota po CEEB_ACCEPT_TIMEOUT_MIN (30 min) — tu zapas na zapis statusu.
const MAX_POLL_MS = 40 * 60 * 1000;

/** Czytelny opis wyniku wysyłki maila ze statusu akceptacji (H4, #189). */
export function mailLabel(mail?: { sent?: boolean; skipped?: string; error?: string }): string | null {
  if (!mail) return null;
  if (mail.sent) return 'Mail: wysłany do zarządcy';
  if (mail.error) return `Mail: nie wysłano (${mail.error})`;
  switch (mail.skipped) {
    case 'disabled': return 'Mail: wyłączony';
    case 'no-client-email':
    case 'no-recipient': return 'Mail: brak adresu zarządcy';
    case 'no-building': return 'Mail: brak budynku';
    default: return mail.skipped ? `Mail: pominięty (${mail.skipped})` : null;
  }
}

export interface UseCeebAcceptPolling {
  /** Czy dla budynku trwa akceptacja uruchomiona z tego widoku. */
  running: Record<string, boolean>;
  start: (buildingId: string, opts?: { acknowledgePending?: boolean }) => Promise<void>;
}

/** `onFinish` dostaje wynik (sukces, błąd bota albo odmowę serwera) — widok decyduje, jak go pokazać. */
export function useCeebAcceptPolling(onFinish: (o: CeebAcceptOutcome) => void): UseCeebAcceptPolling {
  const [running, setRunning] = useState<Record<string, boolean>>({});
  const timers = useRef<Record<string, number>>({});
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;

  useEffect(() => () => {
    Object.values(timers.current).forEach((t) => window.clearInterval(t));
    timers.current = {};
  }, []);

  const finish = useCallback((o: CeebAcceptOutcome) => {
    const t = timers.current[o.buildingId];
    if (t !== undefined) window.clearInterval(t);
    delete timers.current[o.buildingId];
    setRunning((p) => ({ ...p, [o.buildingId]: false }));
    onFinishRef.current(o);
  }, []);

  const start = useCallback(async (buildingId: string, opts: { acknowledgePending?: boolean } = {}) => {
    if (timers.current[buildingId] !== undefined) return;
    setRunning((p) => ({ ...p, [buildingId]: true }));
    let jobId: string;
    try {
      ({ jobId } = await ceebAcceptApi.accept(buildingId, opts));
    } catch (e) {
      finish({ buildingId, status: 'error', error: e instanceof Error ? e.message : 'Nie udało się zaakceptować protokołu' });
      return;
    }
    const startedAt = Date.now();
    timers.current[buildingId] = window.setInterval(async () => {
      if (Date.now() - startedAt > MAX_POLL_MS) {
        finish({ buildingId, status: 'error', error: 'Brak wyniku akceptacji po 40 min — sprawdź status protokołu w CEEB' });
        return;
      }
      try {
        const st = await ceebAcceptApi.status(buildingId);
        // Guard anty-stale: status-file poprzedniego przebiegu tego budynku nie kończy bieżącego.
        if (st.jobId && st.jobId !== jobId) return;
        if (st.status === 'done') finish({ buildingId, status: 'done', mail: st.mail });
        else if (st.status === 'error') finish({ buildingId, status: 'error', error: st.error || 'nieznany błąd' });
      } catch (_) {
        // pojedynczy nieudany poll ignorujemy — limit czasu łapie resztę
      }
    }, POLL_INTERVAL_MS);
  }, [finish]);

  return { running, start };
}
